import React, { useState } from "react";
import { InfoIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { PensionConsent } from "@/types/pensions";

interface PensionSetupConsentProps {
  initialConsent?: PensionConsent;
  termsUrl?: string;
  onConsentSubmit: (consent: PensionConsent) => void;
  isLoading?: boolean;
}

export const PensionSetupConsent: React.FC<PensionSetupConsentProps> = ({
  initialConsent,
  termsUrl = "#", // Default to hash if no URL provided
  onConsentSubmit,
  isLoading = false,
}) => {
  const [hireloContactConsent, setHireloContactConsent] = useState(
    initialConsent?.hireloContactConsent || false,
  );
  const [nestTermsAccepted, setNestTermsAccepted] = useState(
    initialConsent?.nestTermsAccepted || false,
  );

  const handleHireloConsentChange = (checked: boolean) => {
    setHireloContactConsent(checked);
  };

  const handleNestTermsChange = (checked: boolean) => {
    setNestTermsAccepted(checked);
  };

  const handleSubmit = () => {
    const consentData: PensionConsent = {
      ...initialConsent,
      hireloContactConsent,
      nestTermsAccepted,
      termsVersion: "1.0", // Current terms version
    };
    onConsentSubmit(consentData);
  };

  const isSubmitDisabled =
    !hireloContactConsent || !nestTermsAccepted || isLoading;

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Please review and accept the following consents to proceed with your
            pension setup.
          </p>

          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="hirelo-consent"
                checked={hireloContactConsent}
                onCheckedChange={handleHireloConsentChange}
                data-testid="hirelo-consent-checkbox"
              />
              <div className="flex-1">
                <Label
                  htmlFor="hirelo-consent"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  You hereby confirm that you allow Hirelo to create your
                  pension scheme and contact Nest on your behalf
                </Label>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="nest-terms"
                checked={nestTermsAccepted}
                onCheckedChange={handleNestTermsChange}
                data-testid="nest-terms-checkbox"
              />
              <div className="flex-1">
                <Label
                  htmlFor="nest-terms"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  Please read and tick to confirm that you agree with{" "}
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                    data-testid="nest-terms-link"
                  >
                    Nest Pensions terms & conditions
                  </a>
                </Label>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
            <InfoIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700">
              <p className="mb-2">
                <strong>UK Employment Law Requirement:</strong>
              </p>
              <p>
                You're bound by UK employment law to offer a pension scheme to
                your nanny or employees earning above the NI threshold. Your
                nanny can choose to opt out but as an employer, you still must
                offer a pension plan.
              </p>
            </div>
          </div>

          {(!hireloContactConsent || !nestTermsAccepted) && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Required:</strong> Both consent checkboxes must be
                checked to proceed with pension setup.
              </p>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="w-full"
            data-testid="submit-consent-button"
          >
            {isLoading ? "Processing..." : "Confirm Consent & Continue"}
          </Button>
        </div>

        <div className="text-xs text-gray-500 space-y-1">
          <p>By providing this consent, you acknowledge that:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              Hirelo will act on your behalf to set up and manage your pension
              scheme
            </li>
            <li>
              Your information will be shared with Nest Pensions as required
            </li>
            <li>You have read and understood the terms and conditions</li>
            <li>
              This consent is required by UK pension auto-enrolment regulations
            </li>
          </ul>
        </div>
      </div>
    </TooltipProvider>
  );
};
